import React, { useState, useEffect, useCallback } from 'react';
import LoginPage from './pages/LoginPage';
import AdminPage from './pages/AdminPage';
import OperatorPage from './pages/OperatorPage';
import KioskPage from './pages/KioskPage';
import DisplayPage from './pages/DisplayPage';
import DisplayPage_Auth from './pages/DisplayPage_Auth';
import SignupForm from './pages/SignupForm';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate } from 'react-router-dom'; // Import Navigate for redirects
import Footer from './components/Footer'; // <<< NEW: Import Footer component >>>
import axios from 'axios'; 

// Define BASE_URL from environment variable
//const BASE_URL = process.env.REACT_APP_API_BASE_URL; 
// --- Axios Interceptor Setup ---
// This code runs once when the module is loaded (App.js itself).
// It adds user identification headers to every outgoing Axios request if a user is in localStorage.
axios.interceptors.request.use(config => {
  const storedUser = localStorage.getItem('currentUser');
  if (storedUser) {
      try {
          const user = JSON.parse(storedUser);
          // Add custom headers for backend authorization
          config.headers['X-User-Id'] = user.id;
          config.headers['X-User-Role'] = user.role;
          config.headers['X-Tenant-Id'] = user.tenant_id;
      } catch (e) {
          console.error("Failed to parse currentUser from localStorage for interceptor:", e);
          localStorage.removeItem('currentUser'); // Clear invalid data
      }
  }
  return config;
}, error => {
  return Promise.reject(error);
});
// --- End Axios Interceptor Setup ---
// --- Main App Component (Responsible for Auth State and Routing) ---
function App() {
  const [user, setUser] = useState(null); // Central user state
  const [loadingUser, setLoadingUser] = useState(true); // New loading state for initial user check
  const navigate = useNavigate(); // For programmatic navigation

  // --- Initial Load: Read user from localStorage ---
  useEffect(() => {
    const storedUser = localStorage.getItem('currentUser');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      } catch (e) {
        console.error("Failed to parse stored user from localStorage:", e);
        localStorage.removeItem('currentUser'); // Clear invalid data
      }
    }
    setLoadingUser(false); // User loading is complete
  }, []); // Run only once on mount

  // --- Login Handler ---
  const handleLogin = useCallback((userData) => {
    setUser(userData);
    localStorage.setItem('currentUser', JSON.stringify(userData));
    // After login, navigate to the appropriate dashboard
    if (userData.role === 'admin' || userData.role === 'vendor_admin') { // Now includes vendor_admin
      navigate('/admin');
    } else if (userData.role === 'operator') {
      navigate('/operator');
    } else if (userData.role === 'kiosk') {
      navigate('/kiosk');
    } else if (userData.role === 'display') {
      navigate('/display');
    } else {
      navigate('/login'); // Fallback
    }
  }, [navigate]);

  // --- Logout Handler ---
  const handleLogout = useCallback(() => {
    setUser(null); // Clear user state
    localStorage.removeItem('currentUser'); // Clear user from local storage
    navigate('/login'); // Redirect to login page
  }, [navigate]);

  // --- Conditional Rendering for Protected Routes ---
  // A wrapper component to protect routes
  const ProtectedRoute = ({ children, allowedRoles }) => {
    if (loadingUser) {
      return <div>Loading user...</div>; // Show loading while checking localStorage
    }
    if (!user) {
      return <Navigate to="/login" replace />; // Not logged in, redirect to login
    }
    if (allowedRoles && !allowedRoles.includes(user.role)) {
      return ( // User logged in but unauthorized role
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h1>Access Denied</h1>
          <p>You do not have permission to view this page.</p>
          <button onClick={handleLogout} style={{ padding: '10px 20px', cursor: 'pointer' }}>Logout</button>
        </div>
      );
    }
    return children; // User is authenticated and authorized, render content
  };

  // --- Render the main application structure ---
  return (
    <div style={styles.appLayout}>
    <Routes>
      {/* Public routes (accessible without login) */}
      <Route path="/display/:tenantId" element={<DisplayPage />} />
      <Route path="/signup" element={<SignupForm />} />

      {/* Login Page (special case: pass handleLogin) */}
      <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />

      {/* Protected Routes (requires login and specific roles) */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin','vendor_admin']}>
          <AdminPage user={user} onLogout={handleLogout} />
        </ProtectedRoute>
      } />
      <Route path="/operator" element={
        <ProtectedRoute allowedRoles={['operator']}>
          <OperatorPage user={user} /> {/* OperatorPage should handle its own logout */}
        </ProtectedRoute>
      } />
      <Route path="/kiosk" element={
        <ProtectedRoute allowedRoles={['kiosk']}>
          <KioskPage user={user} /> {/* KioskPage usually doesn't have a visible logout */}
        </ProtectedRoute>
      } />
      <Route path="/display" element={
        <ProtectedRoute allowedRoles={['display']}>
          <DisplayPage_Auth user={user} /> 
        </ProtectedRoute>
      } />

      {/* Default/Fallback Route: Redirect authenticated users to their dashboard, others to login */}
      <Route path="/" element={
        loadingUser ? (
          <div>Loading...</div>
        ) : user ? (
          user.role === 'admin'  || user.role === 'vendor_admin' ?  <Navigate to="/admin" replace /> :
          user.role === 'operator' ? <Navigate to="/operator" replace /> :
          user.role === 'kiosk' ? <Navigate to="/kiosk" replace /> :
          user.role === 'display' ? <Navigate to="/display" replace /> :
          <Navigate to="/login" replace /> // Unknown role, redirect to login
        ) : (
          <Navigate to="/login" replace /> // Not logged in, redirect to login
        )
      } />

      {/* Fallback for any unmatched paths (could be a 404 page) */}
      <Route path="*" element={
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h1>Page Not Found</h1>
          <p>The requested URL was not found.</p>
          <button onClick={handleLogout} style={{ padding: '10px 20px', cursor: 'pointer' }}>Logout</button>
        </div>
      } />
    </Routes>
    <Footer />
    </div>
  );
}
// Styles for the overall app layout (pushes footer to bottom)
const styles = {
  appLayout: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh', // Ensure it covers the full viewport height
  }
};
// --- Main entry point for the React app ---
// This ensures BrowserRouter wraps the entire application.
const AppWrapper = () => {
    return (
        <Router>
            <App /> {/* App component manages conditional rendering based on auth */}
        </Router>
    );
}

export default AppWrapper; // Export the wrapper for index.js